import { ReactNode } from "react";
import { Activity, Building2, Calendar, LucideIcon, RefreshCw, Repeat, User } from "lucide-react";
import { ActivityLog } from "@/server-side/types/activityLogs";
import { formatReservationTime, formatRecurringReservationDate } from "@/utils/timeUtils";
import { isValidUUID } from "@/utils/validation";

/**
 * Translator shape required by the formatter: callable for plain strings and
 * `.rich` for messages that wrap the acting user in an <actor> tag.
 */
export interface ActivityTranslator {
  (key: string, values?: Record<string, any>): string;
  rich(key: string, values?: Record<string, any>): ReactNode;
}

/**
 * Accent colors for the acting user, mirroring the reservation calendar
 * (see ReservationItem.getAccentColor): staff-added is red, a registered
 * online user is emerald, anyone else is blue.
 */
export const ACTOR_ACCENT_CLASSES = {
  staff: "text-red-400",
  registered: "text-emerald-400",
  guest: "text-blue-400",
} as const;

export type ActorAccent = keyof typeof ACTOR_ACCENT_CLASSES;

/**
 * Picks the accent for whoever performed the logged action. Staff-initiated
 * entries are attributed to `addedBy`, everything else to the user themselves.
 */
export const getActorAccent = (notification: ActivityLog): ActorAccent => {
  if (notification.addedBy !== notification.userId) return "staff";
  if (notification.userId && isValidUUID(notification.userId)) return "registered";
  return "guest";
};

export const getActorAccentClass = (notification: ActivityLog): string =>
  ACTOR_ACCENT_CLASSES[getActorAccent(notification)];

/**
 * Entity accents. Recurring entries reuse the amber + Repeat pairing from the
 * reservation list (see TYPE_META.recurring in reservations/list/listMeta.tsx)
 * and the calendar's amber accent, so the same thing looks the same everywhere.
 * Everything else stays neutral: the actor name carries the color in a row.
 */
export const ENTITY_ACCENT_CLASSES = {
  recurring: {
    icon: "text-amber-400",
    chip: "bg-amber-500/15",
    badge: "border border-amber-500/20 bg-amber-500/15 text-amber-300",
  },
  neutral: {
    icon: "text-gray-400",
    chip: "bg-gray-500/15",
    badge: "border border-gray-500/20 bg-gray-500/15 text-gray-300",
  },
} as const;

export type EntityAccent = keyof typeof ENTITY_ACCENT_CLASSES;

export const isRecurringActivity = (notification: ActivityLog): boolean =>
  notification.entityType === "RECURRING_RESERVATION";

/**
 * Icon and accent for the kind of thing a log entry is about. Renewals get
 * their own icon so "started a subscription" and "renewed a subscription" are
 * distinguishable without reading the sentence.
 */
export const getActivityEntityMeta = (
  notification: ActivityLog
): { Icon: LucideIcon; accent: EntityAccent } & typeof ENTITY_ACCENT_CLASSES[EntityAccent] => {
  const accent: EntityAccent = isRecurringActivity(notification) ? "recurring" : "neutral";

  let Icon: LucideIcon;
  switch (notification.entityType) {
    case "RECURRING_RESERVATION":
      Icon = notification.actionType === "RENEW" ? RefreshCw : Repeat;
      break;
    case "RESERVATION":
      Icon = Calendar;
      break;
    case "FACILITY":
    case "VENUE":
      Icon = Building2;
      break;
    case "USER":
      Icon = User;
      break;
    default:
      Icon = Activity;
  }

  return { Icon, accent, ...ENTITY_ACCENT_CLASSES[accent] };
};

/**
 * Placeholder names the backend falls back to when it cannot resolve the acting
 * user. They are not localized, so we replace them with a translated subject.
 */
const UNRESOLVED_NAMES = new Set(["user", "unknown user"]);

/**
 * Resolves the name to use as the subject of a staff-initiated message,
 * falling back to a localized "a staff member" when no usable name is known.
 */
export const resolveStaffName = (
  addedByName: string | undefined | null,
  t: (key: string, values?: Record<string, any>) => string
): string => {
  const name = addedByName?.trim();

  if (!name || UNRESOLVED_NAMES.has(name.toLowerCase())) {
    return t("UnknownStaff");
  }

  return name;
};

export const formatActivityMessage = (
  notification: ActivityLog,
  locale: string,
  t: ActivityTranslator
): ReactNode => {
  const isStaffAdded = notification.addedBy !== notification.userId;
  const staffName = resolveStaffName(notification.addedByName, t);
  const accentClass = getActorAccentClass(notification);

  const userDisplay = notification.userName !== "Unknown User"
    ? `${notification.userPhoneNumber}(${notification.userName})`
    : notification.userPhoneNumber;

  // Highlights whoever performed the action, wherever the message places them
  const actor = (chunks: ReactNode) => (
    <span className={`font-medium ${accentClass}`}>{chunks}</span>
  );

  if (notification.entityType === "RESERVATION") {
    // Format time period for reservations if we have start and end dates
    let timeString = "";
    if (notification.startDate && notification.endDate) {
      timeString = formatReservationTime(notification.startDate, notification.endDate, locale);
    }

    switch (notification.actionType) {
      case "CREATE":
        if (timeString) {
          return t.rich(isStaffAdded ? "ReservationCreatedByStaffWithTime" : "ReservationCreatedWithTime", {
            actor,
            staffName,
            userDisplay,
            timeString,
            facilityName: notification.facilityName,
            venueName: notification.venueName
          });
        }
        return t.rich(isStaffAdded ? "ReservationCreatedByStaff" : "ReservationCreated", {
          actor,
          staffName,
          userDisplay,
          facilityName: notification.facilityName,
          venueName: notification.venueName
        });
      case "DELETE":
        if (timeString) {
          return t.rich(isStaffAdded ? "ReservationDeletedByStaffWithTime" : "ReservationDeletedWithTime", {
            actor,
            staffName,
            userDisplay,
            timeString,
            facilityName: notification.facilityName,
            venueName: notification.venueName
          });
        }
        return t.rich(isStaffAdded ? "ReservationDeletedByStaff" : "ReservationDeleted", {
          actor,
          staffName,
          userDisplay,
          facilityName: notification.facilityName,
          venueName: notification.venueName
        });
      default:
        if (timeString) {
          return t.rich(isStaffAdded ? "ReservationUpdatedByStaffWithTime" : "ReservationUpdatedWithTime", {
            actor,
            staffName,
            userDisplay,
            timeString,
            facilityName: notification.facilityName,
            venueName: notification.venueName
          });
        }
        return t.rich(isStaffAdded ? "ReservationUpdatedByStaff" : "ReservationUpdated", {
          actor,
          staffName,
          userDisplay,
          facilityName: notification.facilityName,
          venueName: notification.venueName
        });
    }
  }

  else if (notification.entityType === "RECURRING_RESERVATION") {
    // Format start date only for recurring reservations (no time)
    let startDate = "";
    if (notification.startDate) {
      startDate = formatRecurringReservationDate(notification.startDate, locale);
    }

    switch (notification.actionType) {
      case "CREATE":
        if (startDate) {
          return t.rich(isStaffAdded ? "RecurringReservationCreatedByStaffWithDate" : "RecurringReservationCreatedWithDate", {
            actor,
            staffName,
            userDisplay,
            startDate,
            facilityName: notification.facilityName,
            venueName: notification.venueName
          });
        }
        return t.rich(isStaffAdded ? "RecurringReservationCreatedByStaff" : "RecurringReservationCreated", {
          actor,
          staffName,
          userDisplay,
          facilityName: notification.facilityName,
          venueName: notification.venueName
        });
      case "DELETE":
        if (startDate) {
          return t.rich(isStaffAdded ? "RecurringReservationDeletedByStaffWithDate" : "RecurringReservationDeletedWithDate", {
            actor,
            staffName,
            userDisplay,
            startDate,
            facilityName: notification.facilityName,
            venueName: notification.venueName
          });
        }
        return t.rich(isStaffAdded ? "RecurringReservationDeletedByStaff" : "RecurringReservationDeleted", {
          actor,
          staffName,
          userDisplay,
          facilityName: notification.facilityName,
          venueName: notification.venueName
        });
      case "RENEW":
        if (startDate) {
          return t.rich(isStaffAdded ? "RecurringReservationRenewedByStaffWithDate" : "RecurringReservationRenewedWithDate", {
            actor,
            staffName,
            userDisplay,
            startDate,
            facilityName: notification.facilityName,
            venueName: notification.venueName
          });
        }
        return t.rich(isStaffAdded ? "RecurringReservationRenewedByStaff" : "RecurringReservationRenewed", {
          actor,
          staffName,
          userDisplay,
          facilityName: notification.facilityName,
          venueName: notification.venueName
        });
      default:
        // For other actions on recurring reservations, fallback to generic message
        return `${notification.actionType} action on ${notification.entityType}`;
    }
  }

  // Fallback for other entity types or actions
  return `${notification.actionType} action on ${notification.entityType}`;
};
